import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { ChevronLeft } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "../lib/api";
import { computePrice, formatMoney } from "../lib/pricing";
import { dateToLongLabel, etDateTimeToUtcIso, prettyTime, windowsToSlots } from "../lib/time";
import { loadWizard, saveWizard } from "../lib/wizardState";
import YachtCalendar from "../components/YachtCalendar";

const STEPS = [
  { key: "date", label: "Date" },
  { key: "duration", label: "Duration" },
  { key: "time", label: "Time" },
  { key: "guest", label: "Your details" },
];

const DURATIONS = [4, 5, 6, 7, 8];

export default function Book() {
  const { slug } = useParams();
  const [params] = useSearchParams();
  const navigate = useNavigate();

  const [yacht, setYacht] = useState(null);
  const [step, setStep] = useState(0);
  const [state, setState] = useState(() => {
    const w = loadWizard();
    // Reset if yacht changed
    if (w.slug !== slug) return { slug, date: params.get("date") || null, duration: 4 };
    return { slug, date: params.get("date") || w.date || null, duration: w.duration || 4, ...w };
  });
  const [availWindows, setAvailWindows] = useState([]);
  const [availLoading, setAvailLoading] = useState(false);
  const [guestErrors, setGuestErrors] = useState({});

  // Persist state on every change.
  useEffect(() => {
    saveWizard(state);
  }, [state]);

  // Wizard step transitions stay on the same route, so ScrollToTop won't
  // fire — scroll the window manually. Smooth here (unlike the route-change
  // ScrollToTop) because we're already on the page — no double-frame flash.
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
  }, [step]);

  useEffect(() => {
    api.get(`/yachts/${slug}`)
      .then((r) => setYacht(r.data))
      .catch((e) => toast.error(formatApiErrorDetail(e)));
  }, [slug]);

  // Fetch availability when on the time step.
  useEffect(() => {
    if (step !== 2 || !state.date || !state.duration) return;
    let ignore = false;
    setAvailLoading(true);
    api.get(`/yachts/${slug}/availability`, {
      params: { date: state.date, duration_hours: state.duration },
    })
      .then((r) => !ignore && setAvailWindows(r.data.available_windows || []))
      .catch((e) => {
        if (!ignore) {
          setAvailWindows([]);
          toast.error(formatApiErrorDetail(e));
        }
      })
      .finally(() => !ignore && setAvailLoading(false));
    return () => {
      ignore = true;
    };
  }, [step, slug, state.date, state.duration]);

  const slots = useMemo(() => windowsToSlots(availWindows), [availWindows]);

  const price = useMemo(() => {
    if (!yacht) return null;
    return computePrice({
      basePrice: yacht.base_price,
      minHours: yacht.minimum_duration_hours,
      additionalHourPrice: yacht.additional_hour_price,
      durationHours: state.duration || 4,
    });
  }, [yacht, state.duration]);

  function update(patch) {
    setState((s) => ({ ...s, ...patch }));
  }

  function next() {
    setStep((s) => Math.min(s + 1, STEPS.length - 1));
  }
  function back() {
    setStep((s) => Math.max(s - 1, 0));
  }

  // ------ Step handlers ------
  function onPickDate(d) {
    update({ date: d, time: null });
    next();
  }
  function onPickDuration(hrs) {
    update({ duration: hrs, time: null });
    next();
  }
  function onPickTime(t) {
    update({ time: t });
    next();
  }

  function validateGuest() {
    const errs = {};
    if (!state.customer_name?.trim()) errs.customer_name = "Please enter your full name.";
    if (!state.customer_email?.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(state.customer_email))
      errs.customer_email = "Please enter a valid email.";
    if (!state.customer_phone?.trim()) errs.customer_phone = "Please enter a phone number.";
    const gc = Number(state.guest_count || 0);
    if (!gc || gc < 1) errs.guest_count = "How many people are joining?";
    else if (yacht && gc > yacht.capacity)
      errs.guest_count = `This yacht holds up to ${yacht.capacity} guests.`;
    setGuestErrors(errs);
    return Object.keys(errs).length === 0;
  }

  function reviewBooking() {
    if (!validateGuest()) return;
    // Build UTC iso from ET wall-clock, persist, and navigate to checkout.
    const startIso = etDateTimeToUtcIso(state.date, state.time);
    saveWizard({ ...state, start_datetime_utc: startIso });
    navigate("/checkout");
  }

  if (!yacht) {
    return (
      <div className="hbe-container py-16 text-hbe-ink/60" data-testid="book-loading">
        Loading…
      </div>
    );
  }

  return (
    <div data-testid="book-page" className="hbe-container py-10 md:py-16">
      {/* Progress */}
      <div className="flex items-center gap-3 mb-8">
        <Link
          to={`/yachts/${slug}`}
          className="text-sm text-hbe-ink/70 hover:text-hbe-primary inline-flex items-center gap-1"
          data-testid="book-back-to-detail"
        >
          <ChevronLeft size={16} /> Back to {yacht.name}
        </Link>
      </div>

      <div className="grid gap-3 grid-cols-4 mb-10" data-testid="book-progress">
        {STEPS.map((s, i) => (
          <div
            key={s.key}
            className="flex flex-col gap-1.5"
            data-testid={`step-${s.key}`}
            data-active={i === step}
          >
            <div
              className={`h-1 rounded-full ${
                i <= step ? "bg-hbe-primary" : "bg-hbe-line"
              }`}
            />
            <div
              className={`text-[11px] uppercase tracking-widest ${
                i <= step ? "text-hbe-primary" : "text-hbe-ink/40"
              }`}
            >
              {s.label}
            </div>
          </div>
        ))}
      </div>

      <div className="grid gap-12 lg:grid-cols-[1fr_320px]">
        <div>
          {step === 0 && (
            <section data-testid="step-date-panel">
              <h1 className="font-sans font-semibold text-3xl md:text-4xl">Pick your date</h1>
              <p className="mt-2 text-hbe-ink/70">
                Availability is live. Grey dates are unavailable; a dot means
                partially available.
              </p>
              <div className="mt-8 max-w-md">
                <YachtCalendar
                  yachtKey={yacht.slug}
                  value={state.date}
                  onChange={onPickDate}
                />
              </div>
            </section>
          )}

          {step === 1 && (
            <section data-testid="step-duration-panel">
              <button
                type="button"
                onClick={back}
                className="text-sm text-hbe-ink/70 hover:text-hbe-primary inline-flex items-center gap-1 mb-4"
                data-testid="btn-back"
              >
                <ChevronLeft size={16} /> Back
              </button>
              <h1 className="font-sans font-semibold text-3xl md:text-4xl">
                How long a day?
              </h1>
              <p className="mt-2 text-hbe-ink/70">
                {yacht.minimum_duration_hours}-hour minimum. Extend as far as
                the day allows.
              </p>
              <div className="mt-8 grid gap-3 sm:grid-cols-3 max-w-xl">
                {DURATIONS.map((d) => {
                  const p = computePrice({
                    basePrice: yacht.base_price,
                    minHours: yacht.minimum_duration_hours,
                    additionalHourPrice: yacht.additional_hour_price,
                    durationHours: d,
                  });
                  const selected = state.duration === d;
                  return (
                    <button
                      key={d}
                      type="button"
                      onClick={() => onPickDuration(d)}
                      className={`text-left p-5 rounded-lg border transition ${
                        selected
                          ? "border-hbe-primary bg-hbe-soft/60"
                          : "border-hbe-line hover:border-hbe-accent"
                      }`}
                      data-testid={`duration-${d}`}
                    >
                      <div className="font-sans font-semibold text-2xl text-hbe-primary">{d} hours</div>
                      <div className="mt-1 text-sm text-hbe-ink/70">
                        {formatMoney(p.subtotal)}
                      </div>
                    </button>
                  );
                })}
              </div>
              <p
                className="mt-4 text-xs text-hbe-primary/75"
                data-testid="book-deposit-note"
              >
                {price ? (
                  <>
                    $500 deposit today,{" "}
                    {formatMoney(Math.max(0, price.subtotal - 500))} on your
                    charter day. Total {formatMoney(price.subtotal)} — deposit
                    is applied.
                  </>
                ) : (
                  "$500 deposit today, balance on your charter day. Deposit is applied to your total."
                )}
              </p>
            </section>
          )}

          {step === 2 && (
            <section data-testid="step-time-panel">
              <button
                type="button"
                onClick={back}
                className="text-sm text-hbe-ink/70 hover:text-hbe-primary inline-flex items-center gap-1 mb-4"
                data-testid="btn-back"
              >
                <ChevronLeft size={16} /> Back
              </button>
              <h1 className="font-sans font-semibold text-3xl md:text-4xl">Pick a start time</h1>
              <p className="mt-2 text-hbe-ink/70">
                All times shown in Eastern Time (Miami). Your booking will run
                for {state.duration} hours.
              </p>

              {availLoading ? (
                <div className="mt-8 text-hbe-ink/55" data-testid="time-loading">
                  Loading times…
                </div>
              ) : slots.length === 0 ? (
                <div className="mt-8 hbe-card p-6" data-testid="time-empty">
                  <p className="text-hbe-ink/75">
                    This yacht is unavailable on this date for a {state.duration}-hour
                    booking. Try another date.
                  </p>
                  <button
                    type="button"
                    className="hbe-btn-ghost mt-5"
                    onClick={() => setStep(0)}
                    data-testid="back-to-calendar"
                  >
                    Back to calendar
                  </button>
                </div>
              ) : (
                <div className="mt-8 grid gap-3 grid-cols-3 sm:grid-cols-4 md:grid-cols-5 max-w-2xl">
                  {slots.map((t) => (
                    <button
                      key={t}
                      type="button"
                      onClick={() => onPickTime(t)}
                      className={`py-3 rounded-md border text-sm transition ${
                        state.time === t
                          ? "bg-hbe-primary text-white border-hbe-primary"
                          : "border-hbe-line hover:border-hbe-accent"
                      }`}
                      data-testid={`time-${t}`}
                    >
                      {prettyTime(t)}
                    </button>
                  ))}
                </div>
              )}
            </section>
          )}

          {step === 3 && (
            <section data-testid="step-guest-panel">
              <button
                type="button"
                onClick={back}
                className="text-sm text-hbe-ink/70 hover:text-hbe-primary inline-flex items-center gap-1 mb-4"
                data-testid="btn-back"
              >
                <ChevronLeft size={16} /> Back
              </button>
              <h1 className="font-sans font-semibold text-3xl md:text-4xl">Your details</h1>
              <p className="mt-2 text-hbe-ink/70">
                We'll use these to send confirmation and departure details.
              </p>

              <div className="mt-8 grid gap-5 max-w-xl">
                <div>
                  <label className="hbe-label" htmlFor="g-name">Full name</label>
                  <input
                    id="g-name"
                    className="hbe-input"
                    value={state.customer_name || ""}
                    onChange={(e) => update({ customer_name: e.target.value })}
                    data-testid="guest-name"
                  />
                  {guestErrors.customer_name && (
                    <div className="mt-1 text-sm text-red-700" data-testid="err-name">
                      {guestErrors.customer_name}
                    </div>
                  )}
                </div>
                <div>
                  <label className="hbe-label" htmlFor="g-email">Email</label>
                  <input
                    id="g-email"
                    type="email"
                    className="hbe-input"
                    value={state.customer_email || ""}
                    onChange={(e) => update({ customer_email: e.target.value })}
                    data-testid="guest-email"
                  />
                  {guestErrors.customer_email && (
                    <div className="mt-1 text-sm text-red-700" data-testid="err-email">
                      {guestErrors.customer_email}
                    </div>
                  )}
                </div>
                <div>
                  <label className="hbe-label" htmlFor="g-phone">Phone number</label>
                  <input
                    id="g-phone"
                    className="hbe-input"
                    value={state.customer_phone || ""}
                    onChange={(e) => update({ customer_phone: e.target.value })}
                    data-testid="guest-phone"
                  />
                  {guestErrors.customer_phone && (
                    <div className="mt-1 text-sm text-red-700" data-testid="err-phone">
                      {guestErrors.customer_phone}
                    </div>
                  )}
                </div>
                <div>
                  <label className="hbe-label" htmlFor="g-count">
                    How many people? <span className="text-hbe-ink/50">(max {yacht.capacity})</span>
                  </label>
                  <input
                    id="g-count"
                    type="number"
                    min={1}
                    max={yacht.capacity}
                    className="hbe-input"
                    value={state.guest_count || ""}
                    onChange={(e) => update({ guest_count: e.target.value === "" ? "" : Number(e.target.value) })}
                    data-testid="guest-count"
                  />
                  {guestErrors.guest_count && (
                    <div className="mt-1 text-sm text-red-700" data-testid="err-count">
                      {guestErrors.guest_count}
                    </div>
                  )}
                </div>
                <div>
                  <label className="hbe-label" htmlFor="g-req">
                    Anything we should know? <span className="text-hbe-ink/50">(optional)</span>
                  </label>
                  <textarea
                    id="g-req"
                    rows={3}
                    className="hbe-input resize-y"
                    value={state.special_request || ""}
                    onChange={(e) => update({ special_request: e.target.value })}
                    data-testid="guest-request"
                  />
                </div>
                <div>
                  <label className="hbe-label" htmlFor="g-occ">
                    What are you celebrating? <span className="text-hbe-ink/50">(optional)</span>
                  </label>
                  <input
                    id="g-occ"
                    className="hbe-input"
                    value={state.occasion || ""}
                    onChange={(e) => update({ occasion: e.target.value })}
                    data-testid="guest-occasion"
                  />
                </div>
                <button
                  type="button"
                  onClick={reviewBooking}
                  className="hbe-btn-primary w-full"
                  data-testid="guest-review-btn"
                >
                  Review booking
                </button>
              </div>
            </section>
          )}
        </div>

        {/* Side summary */}
        <aside className="hbe-card p-6 h-fit lg:sticky lg:top-24" data-testid="book-summary">
          <div className="hbe-eyebrow">Your booking</div>
          <h2 className="font-sans font-semibold text-xl mt-1">{yacht.name}</h2>
          <div className="mt-5 space-y-3 text-sm">
            <div>
              <div className="text-hbe-ink/55 text-xs uppercase tracking-widest">Date</div>
              <div className="mt-0.5">
                {state.date ? dateToLongLabel(state.date) : <span className="text-hbe-ink/45">Not selected</span>}
              </div>
            </div>
            <div>
              <div className="text-hbe-ink/55 text-xs uppercase tracking-widest">Duration</div>
              <div className="mt-0.5">{state.duration || "—"} hours</div>
            </div>
            <div>
              <div className="text-hbe-ink/55 text-xs uppercase tracking-widest">Start time (ET)</div>
              <div className="mt-0.5">
                {state.time ? prettyTime(state.time) : <span className="text-hbe-ink/45">Not selected</span>}
              </div>
            </div>
            {price && (
              <div className="pt-4 border-t border-hbe-line">
                <div className="flex justify-between">
                  <span className="text-hbe-ink/70">Estimate</span>
                  <span className="font-medium text-hbe-primary">
                    {formatMoney(price.subtotal)}
                  </span>
                </div>
                <p className="mt-2 text-[11px] text-hbe-ink/50">
                  Server calculates the final price at checkout.
                </p>
              </div>
            )}
          </div>
        </aside>
      </div>
    </div>
  );
}
